import React, { Component } from 'react';
import './index.scss';
import Redux from 'services/gecko-store/redux';
import CONSTS from 'constants/index.js';

const textToNgrams = (n, _text) => {
  const text = _text.content ?? _text;
  // Cast the text to lower case to unweight capital letters
  const lowerCaseText = text.toLowerCase();
  // Pad with 2 spaces to weight starting/ending characters
  const paddedText = `  ${lowerCaseText}  `;
  // Push each group of n characters into an array of trigrams
  let trigrams = [];
  for (var index = paddedText.length - n; index >= 0; index = index - 1) {
    trigrams.push(paddedText.slice(index, index + n));
  }
  return trigrams;
};

const compareNgrams = (gram1, gram2) => {
  // Make an array of all the ngrams across the comparisons
  const all = [].concat(gram1, gram2);
  // Get a count of all the unique trigrams across the comparisons
  const unique = all.filter(
    (item, index) => all.indexOf(item) === index
  ).length;
  // Get a count of all the matchhing trigrams across the comparisons
  const matches = all.length - unique;
  // Finally calculate a confidence score(% match)
  return matches / unique;
};

const pairByNgrams = (n, arr1, arr2) => {
  // Get a permutation for every item in theh first array to it's potential ngrams in the 2nd
  const sorted = arr1
    .map((text) => {
      const ngram = textToNgrams(n, text);
      return arr2.map((testText) => {
        const testNgram = textToNgrams(n, testText);
        const diff = compareNgrams(ngram, testNgram);
        return [text, testText, diff];
      });
    })
    // Flatten out the permutations
    .reduce((flattened, permutations) => {
      return flattened.concat(permutations);
    }, [])
    // Sort the permutations by the highest confidence score
    .sort(([_text1, _test1, diff1], [_text2, _test2, diff2]) => {
      return diff2 - diff1;
    });

  const reduceToPairs = ([next, ...rest], paired = []) => {
    if (!rest.length) {
      return paired;
    }
    const [text, test] = next;
    // Add the next pair in the array to the final paired array
    const newPaired = paired.concat([text, test]);
    // Remove any permutations that are now no longer possible
    const newRest = rest.filter(([restText, restTest]) => {
      return text !== restText && test !== restTest;
    });
    return reduceToPairs(newRest, newPaired);
  };
  const paired = reduceToPairs(sorted);
  // Tack on any items that didn't get paired
  const remaining = arr1.concat(arr2).filter((item) => !paired.includes(item));
  return paired.concat(remaining);
};

const pairByTrigrams = pairByNgrams.bind(null, 3);

export class DeltaChangesWrapper extends Component {
  static defaultProps = {
    className: '',
    limit: null,
    showOverflow: false,
  };

  has(key) {
    if ([CONSTS.DELTA_SKILL_ADD, CONSTS.DELTA_SKILL_REMOVE].includes(key)) {
      return Boolean(this.props.diffs[key]);
    }
    return Array.isArray(this.props.diffs[key]) && this.props.diffs[key].length;
  }

  components = {
    [CONSTS.DELTA_ANSWER_ADD]: {
      className: 'alert-success',
      icon: 'far fa-comment fa-flip-horizontal',
    },
    [CONSTS.DELTA_ANSWER_REMOVE]: {
      className: 'alert-danger',
      icon: 'far fa-comment fa-flip-horizontal',
    },
    [CONSTS.DELTA_SAMPLE_ADD]: {
      className: 'alert-success',
      icon: 'far fa-comments',
    },
    [CONSTS.DELTA_SAMPLE_REMOVE]: {
      className: 'alert-danger',
      icon: 'far fa-comments',
    },
    [CONSTS.DELTA_SKILL_ADD]: {
      className: 'alert-success',
      icon: 'far fa-cog',
    },
    [CONSTS.DELTA_SKILL_REMOVE]: {
      className: 'alert-danger',
      icon: 'far fa-cog',
    },
    [CONSTS.DELTA_TAG_ADD]: { className: 'alert-success', icon: 'far fa-tag' },
    [CONSTS.DELTA_TAG_REMOVE]: {
      className: 'alert-danger',
      icon: 'far fa-tag',
    },
  };

  renderOverflow(showOverflow, limit, diffs) {
    if (!showOverflow || !limit) {
      return null;
    }

    const changeCount = Object.keys(this.components)
      .filter((key) => this.has(key))
      .reduce((counter, key, index) => {
        if ([CONSTS.DELTA_SKILL_ADD, CONSTS.DELTA_SKILL_REMOVE].includes(key)) {
          return counter + 1;
        }
        return counter + this.props.diffs[key].length;
      }, 0);

    const overflowCount = changeCount - this.props.limit;
    if (overflowCount < 1) {
      return null;
    }

    return (
      <div className="DeltaChangesWrapper-overflowCount">
        +{overflowCount} more
      </div>
    );
  }

  convertSkillIdToDiff(skillId) {
    if (skillId === null) {
      return [];
    }
    const { skills } = Redux.store.getState();
    const skill = skills.find((skill) => skill.id === skillId);
    if (!skill) {
      return ['UNKNOWN'];
    }
    return [`${skill.name}`];
  }

  renderPair(addKey, removeKey) {
    const toAdd =
      addKey === CONSTS.DELTA_SKILL_ADD
        ? this.convertSkillIdToDiff(this.props.diffs[addKey])
        : this.props.diffs[addKey];
    const toRemove =
      removeKey === CONSTS.DELTA_SKILL_REMOVE
        ? this.convertSkillIdToDiff(this.props.diffs[removeKey])
        : this.props.diffs[removeKey];
    const pairs = pairByTrigrams(toAdd, toRemove);
    return pairs.reduce((accum, value, index) => {
      const originalKey = toAdd.includes(value) ? addKey : removeKey;
      const config = this.components[originalKey];
      const uniqueId = `${this.props.deltaId}-${originalKey}-${index}`;
      return accum.concat([
        <DeltaChange
          value={value.content ?? value}
          key={`${originalKey}-${index}`}
          uniqueId={uniqueId}
          {...config}
        />,
      ]);
    }, []);
  }

  renderAllPairs() {
    const pairs = [].concat(
      this.renderPair(CONSTS.DELTA_ANSWER_ADD, CONSTS.DELTA_ANSWER_REMOVE),
      this.renderPair(CONSTS.DELTA_SAMPLE_ADD, CONSTS.DELTA_SAMPLE_REMOVE),
      this.renderPair(CONSTS.DELTA_SKILL_ADD, CONSTS.DELTA_SKILL_REMOVE),
      this.renderPair(CONSTS.DELTA_TAG_ADD, CONSTS.DELTA_TAG_REMOVE)
    );
    if (this.props.limit && pairs.length >= this.props.limit) {
      return pairs.slice(0, this.props.limit);
    }
    return pairs;
  }

  render() {
    return (
      <div className={`DeltaChangesWrapper ${this.props.className}`}>
        {this.renderAllPairs()}
        {this.renderOverflow(
          this.props.showOverflow,
          this.props.limit,
          this.props.diffs
        )}
      </div>
    );
  }
}

const DeltaChange = ({ value, className, icon, uniqueId }) => {
  const formattedValue = value.content ? value.content : value;
  return (
    <div
      className={`Delta alert ${className}`}
      id={`DeltaChange-${uniqueId}`}
      title={formattedValue}
    >
      <i className={icon}></i>
      <span>{formattedValue}</span>
    </div>
  );
};

export default DeltaChangesWrapper;
